// labyrinth-view.ts — the Labyrinth: a top-down, pan/zoom map of the whole note
// graph. Notes are "chambers", links are "corridors", dense link-clusters are
// "neighborhoods" (Louvain), links crossing a neighborhood boundary are
// "highways", and referenced-but-uncreated notes appear as distinct phantom
// chambers. Clicking a chamber lights up its full transitive connected web.
//
// Rendered on a single 2D <canvas> with a hand-rolled camera (the pan/zoom math
// mirrors editor/render-core/mermaid.ts). The graph build + clustering + grid
// layout is the headless note-graph-index leaf; this module is the interactive
// shell. Wired as the read-only "Labyrinth" generated view, like the Note Graph.
//
// Interaction: drag a chamber to lift it out of its cluster and drop it on
// another (a placement override saved in .labyrinth); drag empty space to pan;
// click a chamber to light its web; click an uncreated chamber to create it.
// Linking lives in the note TEXT only — "Thread" suggests two similar notes and
// proposes a contextual search-&-replace that wraps a shared term as a link in
// each. Edges + note-name labels show only on hover or for the active web.

import {
  CALENDARS_NOTE,
  clearPendingConflict,
  GRAPH_NOTE,
  getCurrentFileName,
  getLinkedNoteChain,
  LABYRINTH_NOTE,
  mobileMediaQuery,
  previewDiv,
  reflectActiveView,
  saveChain,
} from '../core/app-state.js';
import { NoteEditor } from '../editor/note-editor.js';
import { escapeHtml } from '../utils/sanitize.js';
import { createCardPanel } from '../ui/card-panel.js';
import { activateThreadSurface, deactivateThreadSurface } from '../ui/panels.js';
import { recordLocalEcho } from '../storage/note-local-echo.js';
import { createKeyedList } from '../ui/keyed-list.js';
import { suggestionCell } from './labyrinth-suggest-cell.js';
import {
  addDeclined,
  consumePendingLabyrinthFocus,
  getLabyrinthState,
  type LabyrinthState,
  loadLabyrinthState,
  removeAnchor,
  replaceLabyrinthState,
  setAnchor,
  setLock,
  setZoomTransition,
} from './labyrinth-state.js';
import { findConflictedCopies, isDenyListed } from './conflict-detect.js';
import { mergeConflictContent } from './conflict-merge.js';
import { loadNote } from './note-actions.js';
import {
  buildLabyrinth,
  type Chamber,
  computeSuggestions,
  type LabyrinthModel,
  linkTarget,
  type Neighborhood,
  repointLinks,
  type Suggestion,
  stableHash,
} from './note-graph-index.js';
import {
  bodySimilarity,
  duplicateContentGroups,
  lineDiff,
  topSimilarUnlinkedPairs,
  unionMerge,
} from './note-similarity.js';
import { buildAliasIndex, resolveAlias } from './note-aliases.js';
import {
  findTermSpans,
  hasBareTerm,
  maskedBody,
  replaceBareHits,
  type TermSpan,
  unlinkSpan,
} from './note-mention.js';
import { escapeRegExp } from '../utils/escape-regexp.js';
import { registerToolbarItems, updateToolbarBadge } from '../ui/toolbar.js';

interface Camera {
  scale: number;
  panX: number;
  panY: number;
}
let _persistedCamera: Camera | null = null;
let _persistedFingerprint = '';
// Set by the mounted instance so a reflexive renderLabyrinth() (reflectActiveView
// after a note change) refreshes in place instead of tearing the view down.
let _activeRefresh: (() => Promise<void>) | null = null;
// Set by the mounted instance so a zoom-OUT that arrives while the canvas is
// ALREADY mounted (hidden behind a note — the map persists in the DOM after you
// open a note from it) still plays the intro: frame the source note's node and
// pull the camera back to the whole map. Without this, the reflexive-refresh
// path below skips the tween and leaves the camera wherever the last zoom-IN
// parked it (a close-up on that node).
let _runZoomIntro: ((noteName: string) => void) | null = null;
// The mounted Labyrinth instance exposes its Reset / Thread actions here so the
// contextual-toolbar items (registered once, below) drive whichever instance is
// up. Null when no Labyrinth is mounted — but the items are hidden then anyway.
let _activeControls: { reset: () => void; thread: () => void } | null = null;

// The Labyrinth's toolbar items. Registered at module load so the host has them
// before the view is first entered; they show only in the 'labyrinth' context
// and delegate to the active instance. busyGuard:false — these mutate clustering
// state / open a flow, they aren't the long async note ops the guard is for.
registerToolbarItems([
  {
    id: 'lab-reset',
    label: 'Reset',
    title: 'Re-cluster, ignoring all your moves and dismissed suggestions',
    order: 60,
    contexts: ['labyrinth'],
    busyGuard: false,
    action: () => _activeControls?.reset(),
  },
  {
    id: 'lab-thread',
    label: 'Thread',
    title: 'Walk notes: turn alias terms into links',
    order: 70,
    contexts: ['labyrinth'],
    busyGuard: false,
    action: () => _activeControls?.thread(),
  },
]);

function isReservedNote(name: string): boolean {
  return (
    name.startsWith('.') ||
    name === GRAPH_NOTE ||
    name === LABYRINTH_NOTE ||
    name === CALENDARS_NOTE
  );
}

/** Parse `#rgb`/`#rrggbb` → [r,g,b] (0–255), or null if not a hex color. */
function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/(.)/g, '$1$1') : m[1];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function isDarkTheme(): boolean {
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  const rgb = hexToRgb(bg);
  if (!rgb) return true; // default to dark — the app ships dark-first
  const [r, g, b] = rgb;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5;
}

interface Palette {
  dark: boolean;
  bg: string;
  text: string;
  accent: string;
  muted: string;
  nodeL: number;
  regionL: number;
}

function readPalette(): Palette {
  const cs = getComputedStyle(document.documentElement);
  const dark = isDarkTheme();
  return {
    dark,
    bg: cs.getPropertyValue('--bg').trim() || (dark ? '#1a1620' : '#faf7fc'),
    text: cs.getPropertyValue('--text').trim() || (dark ? '#e8dcf4' : '#2a2230'),
    accent: cs.getPropertyValue('--accent').trim() || '#a272b0',
    muted: cs.getPropertyValue('--muted').trim() || (dark ? '#9a90a8' : '#6a6276'),
    nodeL: dark ? 62 : 52,
    regionL: dark ? 58 : 56,
  };
}

/** Format an `#rrggbb` color as `rgba(...)` at the given alpha (passthrough if unparseable). */
function rgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/** Trace a rounded rectangle path (own impl — ctx.roundRect is absent in jsdom). */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

interface BBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

interface UserAction {
  undo: () => Promise<void> | void;
  redo: () => Promise<void> | void;
}

// A thread walk-through step:
//  • conflict  — a note and its sync "conflicted copy"; show the diff and either
//                3-way merge them or keep one side. Walked FIRST (data integrity).
//  • merge     — a group of same-body/different-title notes to MERGE (keep one
//                title, trash the rest — lossless, the bodies are identical).
//  • mergeDiff — two NEAR-duplicate notes (same note lightly evolved); the user
//                picks a title to keep and either keeps that note's text or
//                combines both losslessly.
//  • aliasNote — one note's alias propagate/create suggestions.
/** A known alias term (`[matt](X)` exists) appearing BARE in this note → link it
 *  to its target note(s); `targets.length > 1` ⇒ the user picks which. */
interface PropSuggestion {
  term: string;
  targets: string[];
}
/** A proper-noun term this note shares with `shared` similar notes that ISN'T an
 *  alias yet → offer to make it one (the user types the target). */
interface CreateSuggestion {
  term: string;
  shared: number;
}
type ThreadItem =
  | { kind: 'merge'; names: string[]; keep: string }
  | { kind: 'mergeDiff'; a: string; b: string; keep: string; combine: boolean; score: number }
  // A note (`base`) and its sync "conflicted copy" (`copy`): show the diff and either 3-way
  // merge them or keep one side. Surfaced FIRST in the walk (data integrity).
  | { kind: 'conflict'; base: string; copy: string; resolution: 'merge' | 'mine' | 'theirs' }
  // One note's actionable alias suggestions — propagate aliases it mentions bare,
  // and create aliases for proper nouns it shares with similar notes.
  | { kind: 'aliasNote'; note: string; props: PropSuggestion[]; creations: CreateSuggestion[] };
type ThreadState = { items: ThreadItem[]; idx: number } | null;

/** The note names a thread item involves — 1 for an alias-note, 2 for a near-dup
 *  pair, N for a duplicate group. */
function threadItemNames(item: ThreadItem | null): string[] {
  if (!item) return [];
  if (item.kind === 'merge') return item.names;
  if (item.kind === 'mergeDiff') return [item.a, item.b];
  if (item.kind === 'conflict') return [item.base, item.copy];
  return [item.note];
}

/** Decline key for one note's alias suggestion of `term`, stored in the shared
 *  `declined` bag. 3 segments keep it distinct from the 2-segment near-dup pairKey,
 *  and note-scoped so a NEW term in the same note still surfaces after a decline. */
function aliasDeclineKey(note: string, term: string): string {
  return `alias\0${note}\0${term}`;
}

// One side of a line-level diff: up to `max` escaped lines, then a "+N more"
// overflow marker. Shared by the merge-diff (max 5) and conflict (max 12) cards.
function diffBlock(label: string, lines: string[], max: number): string {
  if (!lines.length) return '';
  const shown = lines
    .slice(0, max)
    .map((l) => `<div class="labyrinth-md-line">${escapeHtml(l)}</div>`);
  const more =
    lines.length > max ? `<div class="labyrinth-md-more">+${lines.length - max} more</div>` : '';
  return `<div class="labyrinth-md-side"><div class="labyrinth-md-label">${escapeHtml(label)}</div>${shown.join('')}${more}</div>`;
}

/** Near-duplicate gate: bodies at least this char-similar (0..1) are mergeable.
 *  Calibrated against real notes — near-dups score ~0.9+, topical notes ~0.1. */
const NEAR_DUP_SIMILARITY = 0.85;
/** A near-dup is also topically near-identical, so its TF-IDF cosine is well above
 *  this. We only run the expensive char-level bodySimilarity on pairs at/above this
 *  cosine — the long tail can't be a near-dup, sparing ~16× the comparisons. */
const NEAR_DUP_COSINE = 0.5;

/** Render the Labyrinth into the shared #preview surface. */
export async function renderLabyrinth(): Promise<void> {
  // Reflexive re-render (e.g. reflectActiveView after the view's own write):
  // refresh the mounted instance in place rather than tearing down its DOM and
  // transient UI (selection, thread flow). A fresh open falls through.
  if (_activeRefresh && document.getElementById('labyrinth-container')) {
    await _activeRefresh();
    // If this refresh is actually a zoom-OUT into the already-mounted map, play
    // the intro on the live instance (a rAF so it runs after note-manager flips
    // the canvas back to display:block and its size is real).
    const refocusNote = consumePendingLabyrinthFocus();
    if (refocusNote && _runZoomIntro) {
      const run = _runZoomIntro;
      requestAnimationFrame(() => run(refocusNote));
    }
    return;
  }
  previewDiv.innerHTML = '';
  previewDiv.style.overflow = 'hidden';

  const allNotes = await window.NoteStorage.getAllNotes();
  let notes = allNotes.filter((n: { name: string }) => !isReservedNote(n.name));

  if (notes.length === 0) {
    const hint = document.createElement('div');
    hint.className = 'labyrinth-empty';
    hint.innerHTML =
      'No notes to map yet.<br>Write a few notes and link them with ' +
      '<code>[[Note Name]]</code> to see your labyrinth take shape.';
    previewDiv.appendChild(hint);
    return;
  }

  await loadLabyrinthState();
  // Previous Leiden partition, fed back on every rebuild so clustering WARM-STARTS — a small
  // edit re-clusters locally instead of globally re-partitioning (stable membership). Cleared on
  // Reset for a deliberate cold re-cluster.
  let lastPartition: Record<string, number> | undefined;
  let model: LabyrinthModel = buildLabyrinth(notes, {
    state: getLabyrinthState(),
    prevPartition: lastPartition,
  });
  lastPartition = model.partition;
  // Per-build derived indexes (id→neighborhood, id→tight bbox), rebuilt by
  // indexModel() on every model rebuild so draw()/hit-testing stay O(1) per frame.
  let nbById = new Map<string, Neighborhood>();
  let nbBox = new Map<string, BBox>();
  indexModel();
  let suggestions: Suggestion[] = computeSuggestions(model, getLabyrinthState());
  const palette = readPalette();

  const container = document.createElement('div');
  container.id = 'labyrinth-container';
  previewDiv.appendChild(container);

  const canvas = document.createElement('canvas');
  canvas.id = 'labyrinth-canvas';
  container.appendChild(canvas);
  const maybeCtx = canvas.getContext('2d');
  if (!maybeCtx) return;
  const ctx: CanvasRenderingContext2D = maybeCtx;

  const suggestPanel = createCardPanel({ className: 'labyrinth-suggest' });
  const suggestHead = document.createElement('div');
  suggestHead.className = 'card-panel-head';
  suggestPanel.appendChild(suggestHead);
  const suggestListEl = document.createElement('div');
  suggestListEl.className = 'labyrinth-suggest-list';
  suggestPanel.appendChild(suggestListEl);
  container.appendChild(suggestPanel);
  const suggestList = createKeyedList(suggestListEl);

  // The Thread walk renders into the side-panel #thread-panel surface (docked into
  // #thread-container on desktop / a bottom sheet on mobile — see ui/panels.ts),
  // NOT a floating card over the canvas.
  const threadPanel = document.getElementById('thread-panel') as HTMLElement;
  // Per-step disclosure + input state (rebuilt on each step change): which folds are
  // open, the chosen prop/create target per term, and the mobile one-at-a-time index.
  const threadOpen = new Set<string>();
  const threadTargets = new Map<string, string>();
  let mobileStepIdx = 0;

  // Native autocomplete for "create alias → type a target note". Populated when a
  // thread starts; the create-row inputs reference it by id.
  const noteDatalist = document.createElement('datalist');
  noteDatalist.id = 'labyrinth-note-names';
  container.appendChild(noteDatalist);

  // ── Camera ────────────────────────────────────────────────────────────────
  const camera: Camera = { scale: 1, panX: 0, panY: 0 };
  const fingerprint = `${notes.length}:${stableHash(
    notes
      .map((n: any) => n.name)
      .sort()
      .join(' ')
  )}`;

  let cssW = 0;
  let cssH = 0;

  function resizeCanvas(): void {
    const rect = container.getBoundingClientRect();
    cssW = Math.max(1, rect.width);
    cssH = Math.max(1, rect.height);
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
  }

  /** Camera pose that frames bbox `b` with a margin (capped at 1.6× zoom). */
  function fitBoxTarget(b: BBox): Camera {
    const w = b.maxX - b.minX || 1;
    const h = b.maxY - b.minY || 1;
    const margin = 80;
    const scale = Math.max(0.02, Math.min((cssW - margin) / w, (cssH - margin) / h, 1.6));
    return {
      scale,
      panX: cssW / 2 - ((b.minX + b.maxX) / 2) * scale,
      panY: cssH / 2 - ((b.minY + b.maxY) / 2) * scale,
    };
  }
  function fitBox(b: BBox): void {
    const t = fitBoxTarget(b);
    camera.scale = t.scale;
    camera.panX = t.panX;
    camera.panY = t.panY;
  }

  const worldToScreenX = (x: number) => x * camera.scale + camera.panX;
  const worldToScreenY = (y: number) => y * camera.scale + camera.panY;
  const screenToWorldX = (x: number) => (x - camera.panX) / camera.scale;
  const screenToWorldY = (y: number) => (y - camera.panY) / camera.scale;

  // ── Camera tween — the animated note↔node zoom ────────────────────────────
  let cameraTweenId: number | null = null;
  const _lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const _easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
  const _reducedMotion = () =>
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Position tween — on a rebuild, glide each chamber from where it WAS to where the new
  //    layout puts it, so a note that moved clusters visibly travels and a growing territory
  //    eases into place (the spatial-memory layout barely moves, so most chambers sit still).
  //    Mutates model.chambers each frame and busts the scene cache; brand-new chambers (no
  //    previous spot) just appear at their target. Snaps instantly under reduced-motion.
  let posTweenId: number | null = null;
  function animatePositions(fromPos: Map<string, { x: number; y: number }>): void {
    if (posTweenId != null) cancelAnimationFrame(posTweenId);
    posTweenId = null;
    const toPos = new Map<string, { x: number; y: number }>();
    let moved = 0;
    for (const [name, c] of model.chambers) {
      toPos.set(name, { x: c.x, y: c.y });
      const f = fromPos.get(name);
      if (f && (Math.abs(f.x - c.x) > 0.5 || Math.abs(f.y - c.y) > 0.5)) moved++;
    }
    if (_reducedMotion() || moved === 0) {
      requestDraw();
      return;
    }
    const dur = 540;
    const t0 = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - t0) / dur);
      const e = _easeInOutCubic(t);
      for (const [name, c] of model.chambers) {
        const f = fromPos.get(name);
        const to = toPos.get(name);
        if (f && to) {
          c.x = _lerp(f.x, to.x, e);
          c.y = _lerp(f.y, to.y, e);
        }
      }
      sceneKey = ''; // chamber positions change every frame → re-render, don't reuse the cache
      draw();
      if (t < 1) {
        posTweenId = requestAnimationFrame(step);
      } else {
        posTweenId = null;
        for (const [name, c] of model.chambers) {
          const to = toPos.get(name);
          if (to) {
            c.x = to.x;
            c.y = to.y;
          }
        }
        draw();
      }
    };
    posTweenId = requestAnimationFrame(step);
  }

  /** Camera pose centering chamber `c`, filling ~`fill` of the shorter screen axis. */
  function nodeFillTarget(c: Chamber, fill = 0.55): Camera {
    const s = Math.max(0.02, Math.min(6, Math.min(cssW, cssH) / ((2 * c.radius) / fill)));
    return { scale: s, panX: cssW / 2 - c.x * s, panY: cssH / 2 - c.y * s };
  }

  // Tween the camera to `to` over `duration` ms on a DEDICATED rAF (not
  // requestDraw, which coalesces to one frame). Scale interpolates in log space
  // so a deep zoom reads as a constant-rate change of altitude rather than a
  // lurch; pan is linear (both endpoints already encode their own centering).
  // Honors prefers-reduced-motion (snaps); cancellable via cameraTweenId.
  function animateCamera(
    to: Camera,
    opts: {
      duration?: number;
      focus?: { x: number; y: number };
      onFrame?: (e: number, cam: Camera) => void;
      onDone?: () => void;
    } = {}
  ): void {
    if (cameraTweenId != null) cancelAnimationFrame(cameraTweenId);
    cameraTweenId = null;
    if (_reducedMotion()) {
      camera.scale = to.scale;
      camera.panX = to.panX;
      camera.panY = to.panY;
      opts.onFrame?.(1, camera);
      draw();
      opts.onDone?.();
      return;
    }
    const from = { scale: camera.scale, panX: camera.panX, panY: camera.panY };
    const dur = opts.duration ?? 560;
    // Pivot zoom: when a focus world-point is given, glide ITS screen position
    // linearly from start to end while the scale zooms in log space, deriving pan
    // each frame so the focus stays put-ish — otherwise (scale geometric, pan
    // linear) the focus swings far off-screen mid-tween. This is what makes the
    // map AND the billboarded note move smoothly around the node.
    const f = opts.focus;
    const fsx0 = f ? f.x * from.scale + from.panX : 0;
    const fsy0 = f ? f.y * from.scale + from.panY : 0;
    const fsx1 = f ? f.x * to.scale + to.panX : 0;
    const fsy1 = f ? f.y * to.scale + to.panY : 0;
    const t0 = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - t0) / dur);
      const e = _easeInOutCubic(t);
      camera.scale = Math.exp(_lerp(Math.log(from.scale), Math.log(to.scale), e));
      if (f) {
        camera.panX = _lerp(fsx0, fsx1, e) - f.x * camera.scale;
        camera.panY = _lerp(fsy0, fsy1, e) - f.y * camera.scale;
      } else {
        camera.panX = _lerp(from.panX, to.panX, e);
        camera.panY = _lerp(from.panY, to.panY, e);
      }
      opts.onFrame?.(e, camera); // ride the SAME clock: drive the editor billboard
      draw();
      if (t < 1) {
        cameraTweenId = requestAnimationFrame(step);
      } else {
        cameraTweenId = null;
        opts.onDone?.();
      }
    };
    cameraTweenId = requestAnimationFrame(step);
  }

  // ── The note "billboard" (the Street-View zoom) ───────────────────────────
  // The note (#cm-editor) is rendered as a flat card pinned to its chamber and
  // transformed each frame off the SAME camera tween, so the note and the map
  // move as one: the note shrinks INTO its node as the map pulls back (zoom-OUT)
  // and grows OUT of its node as the map zooms in (zoom-IN). `s0` is the node-fill
  // camera scale, so editorScale = camera.scale / s0 is 1 at the node-fill pose
  // (note fills the screen, transform ≈ identity → no jump against the real
  // editor) and tiny at the whole-map pose. translate keeps the note centred on
  // the node's LIVE screen position. Transform the HOST only (CM measures its own
  // layout boxes, which a transform doesn't change); reset before it's
  // interactive again so caret/click hit-testing never sees a scaled rect.
  function applyBillboard(c: Chamber, s0: number, cam: Camera, dir: 'in' | 'out', e: number): void {
    const ed = document.getElementById('cm-editor');
    if (!ed) return;
    if (_reducedMotion()) {
      ed.style.transform = '';
      ed.style.opacity = '';
      return;
    }
    const sx = c.x * cam.scale + cam.panX; // node screen-x (canvas-local CSS px)
    const sy = c.y * cam.scale + cam.panY;
    const k = cam.scale / s0;
    ed.style.transformOrigin = '50% 50%';
    ed.style.transform = `translate(${sx - cssW / 2}px, ${sy - cssH / 2}px) scale(${k})`;
    // Fade only at the node end of the travel so the note dissolves once it has
    // already shrunk onto (zoom-out) / before it grows out of (zoom-in) the node.
    ed.style.opacity = String(
      dir === 'out' ? (e <= 0.7 ? 1 : 1 - (e - 0.7) / 0.3) : e >= 0.3 ? 1 : e / 0.3
    );
  }
  function resetBillboard(): void {
    const ed = document.getElementById('cm-editor');
    if (!ed) return;
    ed.style.transform = '';
    ed.style.opacity = '';
    ed.style.transformOrigin = '';
  }

  // The zoom-OUT intro, shared by the fresh mount and the reflexive-refresh path
  // (`_runZoomIntro`): clear any restored selection, frame the source note's node,
  // then pull the camera back to the whole map. Falls back to a plain whole-map
  // fit when the node is gone. Re-measures first so it's correct even when the
  // canvas was just un-hidden.
  function runZoomOutIntro(noteName: string): void {
    resizeCanvas();
    selectChamber(null);
    fitBox(model.bounds);
    const c = model.chambers.get(noteName);
    if (c && !c.missing) {
      const s0 = nodeFillTarget(c).scale; // the billboard's k=1 (node-fill) pose
      const target: Camera = { scale: camera.scale, panX: camera.panX, panY: camera.panY };
      Object.assign(camera, nodeFillTarget(c)); // start framed on the node…
      applyBillboard(c, s0, camera, 'out', 0); // …note at identity (no jump) before the seed paint
      draw();
      animateCamera(target, {
        focus: { x: c.x, y: c.y }, // pivot on the node so it glides smoothly to its map spot
        onFrame: (e, cam) => applyBillboard(c, s0, cam, 'out', e),
        onDone: () => {
          NoteEditor.setVisible(false); // drop the (now tiny + faded) note under the map
          resetBillboard();
          document.getElementById('editor-section')?.classList.remove('zoom-billboard');
        },
      });
    } else {
      draw();
    }
  }

  // ── State ───────────────────────────────────────────────────────────────
  let highlight: Set<string> | null = null;
  let selected: string | null = null;
  let hovered: string | null = null;
  /** the chamber being dragged out of its cluster, if any. */
  let nodeDrag: { name: string; world: { x: number; y: number } } | null = null;
  let dropTarget: Neighborhood | null = null;
  let thread: ThreadState = null;
  // Camera pose the thread walk is animating TOWARD — so the floating card can be
  // placed where the node WILL be, not where it is mid-zoom.
  let _focusCam: Camera | null = null;
  const undoStack: UserAction[] = [];
  let redoStack: UserAction[] = [];
  let _writing = false; // single-flight guard for note-write flows (create / link)
  const isTouchOnly = window.matchMedia('(hover: none)').matches;

  // Rebuild the id→neighborhood + id→tight-bbox indexes from the current model.
  function indexModel(): void {
    nbById = new Map();
    nbBox = new Map();
    for (const nb of model.neighborhoods) {
      nbById.set(nb.id, nb);
      // The cluster region IS its packed grid block (the tint + drop zone). The block
      // bounds every member circle, and because clusters pack a uniform gap apart, using
      // the block — not the tight circle hull — makes the visible tint gaps pixel-uniform.
      const bl = nb.block;
      nbBox.set(nb.id, { minX: bl.x, minY: bl.y, maxX: bl.x + bl.w, maxY: bl.y + bl.h });
    }
  }

  function regionBBox(nb: Neighborhood, pad = 20): BBox | null {
    // nbBox already encloses node circles; `pad` is just breathing room.
    const b = nbBox.get(nb.id);
    if (!b) return null;
    return { minX: b.minX - pad, minY: b.minY - pad, maxX: b.maxX + pad, maxY: b.maxY + pad };
  }

  function neighborhoodAt(wx: number, wy: number): Neighborhood | null {
    for (const nb of model.neighborhoods) {
      const b = regionBBox(nb);
      if (b && wx >= b.minX && wx <= b.maxX && wy >= b.minY && wy <= b.maxY) return nb;
    }
    return null;
  }

  // ── Draw ──────────────────────────────────────────────────────────────────
  let drawScheduled = false;
  function requestDraw(): void {
    if (drawScheduled) return;
    drawScheduled = true;
    requestAnimationFrame(() => {
      drawScheduled = false;
      draw();
    });
  }

  // Offscreen snapshot of the last fully-rendered scene (see draw()). Hovering only
  // repaints the pointed label over this, so sweeping is a blit + one label rather
  // than re-rendering every chamber.
  const sceneCanvas = document.createElement('canvas');
  const sceneCtx = sceneCanvas.getContext('2d') as CanvasRenderingContext2D;
  let sceneKey = '';
  // Label widths are stable (fixed 12px font), so measure each name once.
  const _labelW = new Map<string, number>();
  function labelWidth(name: string): number {
    let w = _labelW.get(name);
    if (w === undefined) {
      w = ctx.measureText(name).width;
      _labelW.set(name, w);
    }
    return w;
  }

  // Draw the focused note's connections as right-angle "highways" that follow the
  // cluster grid: group its linked notes by cluster and, for each OTHER cluster,
  // run a trunk at right angles to a SPINE on the cluster's near edge, then tap
  // each linked note off that spine with a short perpendicular stub (a comb/bus).
  // Same-cluster links and lone far links are plain single-elbow connectors. This
  // turns a hub note's straight-line starburst into a tidy orthogonal layout.
  function drawHighways(focus: string): void {
    const fc = model.chambers.get(focus);
    if (!fc) return;
    const neighbors = model.adjacency.get(focus);
    if (!neighbors || neighbors.size === 0) return;
    const byGroup = new Map<string, Chamber[]>();
    for (const nm of neighbors) {
      const nc = model.chambers.get(nm);
      if (!nc) continue;
      let arr = byGroup.get(nc.neighborhoodId);
      if (!arr) {
        arr = [];
        byGroup.set(nc.neighborhoodId, arr);
      }
      arr.push(nc);
    }
    const STUB = 18; // world-unit gap between the spine and the cluster's edge

    // One-elbow orthogonal path: travel the larger delta first, then turn 90°.
    const elbow = (ax: number, ay: number, bx: number, by: number): void => {
      ctx.moveTo(ax, ay);
      if (Math.abs(bx - ax) >= Math.abs(by - ay)) ctx.lineTo(bx, ay);
      else ctx.lineTo(ax, by);
      ctx.lineTo(bx, by);
    };

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const [gid, members] of byGroup) {
      if (gid === fc.neighborhoodId || members.length === 1) {
        // Same cluster as the focus, or a single far link → a plain elbow each.
        const same = gid === fc.neighborhoodId;
        ctx.strokeStyle = rgba(palette.accent, same ? 0.6 : 0.85);
        ctx.lineWidth = (same ? 1.6 : 2.4) / camera.scale;
        ctx.beginPath();
        for (const m of members) elbow(fc.x, fc.y, m.x, m.y);
        ctx.stroke();
        continue;
      }
      // Comb: trunk → spine on the edge facing the focus → perpendicular stubs.
      let minX = Number.POSITIVE_INFINITY;
      let minY = Number.POSITIVE_INFINITY;
      let maxX = Number.NEGATIVE_INFINITY;
      let maxY = Number.NEGATIVE_INFINITY;
      for (const m of members) {
        if (m.x < minX) minX = m.x;
        if (m.x > maxX) maxX = m.x;
        if (m.y < minY) minY = m.y;
        if (m.y > maxY) maxY = m.y;
      }
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      // Spine perpendicular to the trunk's approach: vertical when the focus is
      // mostly left/right of the cluster, horizontal when mostly above/below.
      const verticalSpine = Math.abs(cx - fc.x) >= Math.abs(cy - fc.y);

      ctx.strokeStyle = rgba(palette.accent, 0.85);
      ctx.lineWidth = 2.4 / camera.scale; // trunk + spine
      ctx.beginPath();
      let spineX = 0;
      let spineY = 0;
      if (verticalSpine) {
        spineX = fc.x < cx ? minX - STUB : maxX + STUB;
        ctx.moveTo(fc.x, fc.y); // trunk: horizontal in to the spine
        ctx.lineTo(spineX, fc.y);
        ctx.moveTo(spineX, Math.min(minY, fc.y)); // spine (extended to the trunk join)
        ctx.lineTo(spineX, Math.max(maxY, fc.y));
      } else {
        spineY = fc.y < cy ? minY - STUB : maxY + STUB;
        ctx.moveTo(fc.x, fc.y); // trunk: vertical in to the spine
        ctx.lineTo(fc.x, spineY);
        ctx.moveTo(Math.min(minX, fc.x), spineY); // spine (extended to the trunk join)
        ctx.lineTo(Math.max(maxX, fc.x), spineY);
      }
      ctx.stroke();

      ctx.strokeStyle = rgba(palette.accent, 0.5);
      ctx.lineWidth = 1.4 / camera.scale;
      ctx.beginPath();
      if (verticalSpine) {
        for (const m of members) {
          ctx.moveTo(spineX, m.y);
          ctx.lineTo(m.x, m.y);
        }
      } else {
        for (const m of members) {
          ctx.moveTo(m.x, spineY);
          ctx.lineTo(m.x, m.y);
        }
      }
      ctx.stroke();
    }
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
  }

  function drawChamber(c: Chamber): void {
    const nb = nbById.get(c.neighborhoodId);
    const lit = highlight === null || highlight.has(c.name);
    ctx.beginPath();
    ctx.arc(c.x, c.y, c.radius, 0, Math.PI * 2);
    if (c.missing) {
      // Uncreated note → a faint, BORDERLESS muted disc by default. When it's
      // selected or part of an active selection tree, give it a MUTED COLOUR (a
      // desaturated tint of its cluster) so it reads as part of the web without
      // looking like a real, created note.
      const inTree = highlight?.has(c.name) ?? false;
      if (inTree) {
        ctx.fillStyle =
          nb && !nb.isMisc ? `hsl(${nb.hue}, 28%, ${palette.nodeL}%)` : rgba(palette.muted, 0.6);
      } else {
        ctx.fillStyle = rgba(palette.muted, highlight === null ? 0.2 : 0.08);
      }
      ctx.fill();
    } else {
      const baseFill =
        nb && !nb.isMisc
          ? `hsl(${nb.hue}, 45%, ${palette.nodeL}%)`
          : `hsl(0, 0%, ${palette.dark ? 46 : 60}%)`;
      ctx.fillStyle = lit ? baseFill : rgba(palette.text, palette.dark ? 0.16 : 0.12);
      ctx.fill();
    }
    // No borders: a highlighted node is shown purely by staying in colour while
    // the rest of the map dims (see the `highlight`/`dim` computation in draw()).
  }

  // The full scene, MINUS the hovered label (chambers, regions, the selected note's
  // connection-tree highways, the dim, and every always-on note label). Painted to the
  // visible canvas, then snapshotted by draw(). Re-run only when something other than
  // hover changes (sceneKeyOf()).
  function renderScene(): void {
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(
      dpr * camera.scale,
      0,
      0,
      dpr * camera.scale,
      dpr * camera.panX,
      dpr * camera.panY
    );
    ctx.fillStyle = palette.bg;
    const vx0 = screenToWorldX(0);
    const vy0 = screenToWorldY(0);
    const vw = cssW / camera.scale;
    const vh = cssH / camera.scale;
    ctx.fillRect(vx0, vy0, vw, vh);

    // Highlight = the SELECTED note (a click, not hover) + its DIRECT links, plus any
    // thread pair under review. Everything else dims; no node borders are ever used — a
    // highlighted node is shown purely by keeping its colour while the rest fades. Hover
    // deliberately does NOT fade the map (it only surfaces a name, in draw()), so
    // sweeping stays snappy; the fade is a click action that persists the connection tree.
    const activeNames = threadItemNames(thread ? thread.items[thread.idx] : null);
    const focus = selected;
    let litSet: Set<string> | null = null;
    if (focus && model.chambers.has(focus)) {
      litSet = new Set<string>([focus]);
      for (const nm of model.adjacency.get(focus) ?? []) litSet.add(nm);
    }
    if (activeNames.length >= 1) {
      litSet ??= new Set<string>();
      for (const n of activeNames) litSet.add(n);
    }
    highlight = litSet;
    const dim = highlight !== null;

    // Neighborhood region tints. The drop target (during a node drag) gets an
    // accent ring to show where it'll land.
    for (const nb of model.neighborhoods) {
      const b = regionBBox(nb);
      if (!b) continue;
      const baseA = nb.isMisc ? 0.05 : 0.07;
      const a = baseA * (dim ? 0.5 : 1);
      ctx.fillStyle = nb.isMisc
        ? `hsla(0, 0%, ${palette.regionL}%, ${a})`
        : `hsla(${nb.hue}, 50%, ${palette.regionL}%, ${a})`;
      roundRect(ctx, b.minX, b.minY, b.maxX - b.minX, b.maxY - b.minY, 24);
      ctx.fill();
      if (dropTarget && dropTarget.id === nb.id && !nb.isMisc) {
        ctx.lineWidth = 2 / camera.scale;
        ctx.strokeStyle = rgba(palette.accent, 0.8);
        roundRect(ctx, b.minX, b.minY, b.maxX - b.minX, b.maxY - b.minY, 24);
        ctx.stroke();
      }
    }

    const cull = { x0: vx0 - 40, y0: vy0 - 40, x1: vx0 + vw + 40, y1: vy0 + vh + 40 };
    const inView = (c: Chamber) =>
      c.x >= cull.x0 && c.x <= cull.x1 && c.y >= cull.y0 && c.y <= cull.y1;

    // ── Connections: the focused note's links as bundled "highways" — one trunk
    //    per destination cluster that fans out to the linked notes inside it ────
    if (focus) drawHighways(focus);

    // ── Chambers ──────────────────────────────────────────────────────────
    for (const c of model.chambers.values()) {
      if (!inView(c)) continue;
      if (nodeDrag && nodeDrag.name === c.name) continue; // drawn lifted at the cursor
      drawChamber(c);
    }

    // Lifted (dragged) chamber follows the cursor.
    if (nodeDrag) {
      const c = model.chambers.get(nodeDrag.name);
      if (c) {
        const lifted = { ...c, x: nodeDrag.world.x, y: nodeDrag.world.y };
        ctx.save();
        ctx.shadowColor = rgba('#000000', 0.35);
        ctx.shadowBlur = 10 / camera.scale;
        ctx.shadowOffsetY = 4 / camera.scale;
        drawChamber(lifted);
        ctx.restore();
      }
    }

    // Thread item under review — link pair (2 members) or merge group (N members).
    // The pair is spotlit by the dim above (it's in `activeNames`); here we only
    // draw the dashed link between them — no rings around the nodes.
    const activeCh = activeNames
      .map((n) => model.chambers.get(n))
      .filter((c): c is Chamber => Boolean(c));
    if (activeCh.length >= 2) {
      // Star from the first member to every other — a single dash for a pair, a
      // tight burst for a 3+ duplicate group.
      ctx.lineWidth = 2.4 / camera.scale;
      ctx.strokeStyle = rgba(palette.accent, 0.95);
      ctx.setLineDash([10 / camera.scale, 7 / camera.scale]);
      ctx.beginPath();
      for (let i = 1; i < activeCh.length; i++) {
        ctx.moveTo(activeCh[0].x, activeCh[0].y);
        ctx.lineTo(activeCh[i].x, activeCh[i].y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // ── Screen-space labels: the thread pair under review (Thread walk) plus the
    // SELECTED main note's own name. The selected note's connection TREE is not
    // auto-labelled — like the rest of the map, those names appear on hover (the hovered
    // name is painted on top, in draw()). Full names, collision-avoided so none overlap.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    const placed: BBox[] = [];
    const seenLabel = new Set<string>();
    const queueLabel = (name: string): void => {
      if (seenLabel.has(name)) return;
      seenLabel.add(name);
      const c = model.chambers.get(name);
      if (!c) return;
      const sx = worldToScreenX(c.x);
      const sy = worldToScreenY(c.y) - c.radius * camera.scale - 8;
      if (sx < -40 || sx > cssW + 40 || sy < -20 || sy > cssH + 20) return;
      const w = labelWidth(c.name);
      const box = { minX: sx - w / 2 - 3, minY: sy - 8, maxX: sx + w / 2 + 3, maxY: sy + 8 };
      for (const p of placed) {
        if (!(box.maxX < p.minX || box.minX > p.maxX || box.maxY < p.minY || box.minY > p.maxY)) {
          return;
        }
      }
      placed.push(box);
      ctx.lineWidth = 3;
      ctx.strokeStyle = rgba(palette.bg, 0.85);
      ctx.strokeText(c.name, sx, sy);
      ctx.fillStyle = c.missing ? palette.muted : palette.text;
      ctx.fillText(c.name, sx, sy);
    };
    for (const name of activeNames) queueLabel(name);
    if (selected) queueLabel(selected); // the selected MAIN note keeps its label; its tree appears on hover
  }

  // Just the hovered note's name, full, on top of everything — the per-frame overlay
  // that makes hover-sweeping feel instant without re-rendering the scene.
  function drawHoveredLabel(): void {
    if (!hovered) return;
    const c = model.chambers.get(hovered);
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    const sx = worldToScreenX(c.x);
    const sy = worldToScreenY(c.y) - c.radius * camera.scale - 8;
    if (sx < -40 || sx > cssW + 40 || sy < -20 || sy > cssH + 20) return;
    ctx.lineWidth = 3;
    ctx.strokeStyle = rgba(palette.bg, 0.85);
    ctx.strokeText(c.name, sx, sy);
    ctx.fillStyle = c.missing ? palette.muted : palette.text;
    ctx.fillText(c.name, sx, sy);
  }

  // Everything the scene snapshot depends on EXCEPT hover. Same key ⇒ reuse the
  // snapshot (a cheap hover repaint); changed ⇒ re-render the scene.
  function sceneKeyOf(): string {
    const drag = nodeDrag
      ? `${nodeDrag.name}:${Math.round(nodeDrag.world.x)}:${Math.round(nodeDrag.world.y)}`
      : '';
    return `${selected}|${camera.scale}|${Math.round(camera.panX)}|${Math.round(camera.panY)}|${drag}|${thread ? thread.idx : ''}|${dropTarget?.id ?? ''}|${palette.bg}|${canvas.width}`;
  }

  function draw(): void {
    const key = sceneKeyOf();
    if (key !== sceneKey) {
      renderScene();
      if (sceneCanvas.width !== canvas.width || sceneCanvas.height !== canvas.height) {
        sceneCanvas.width = canvas.width;
        sceneCanvas.height = canvas.height;
      }
      sceneCtx.setTransform(1, 0, 0, 1, 0, 0);
      sceneCtx.clearRect(0, 0, sceneCanvas.width, sceneCanvas.height);
      sceneCtx.drawImage(canvas, 0, 0);
      sceneKey = key;
    } else {
      // Hover-only repaint: restore the cached scene, then the pointed label on top.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(sceneCanvas, 0, 0);
    }
    drawHoveredLabel();
  }

  // ── Rebuild ───────────────────────────────────────────────────────────────
  function rebuildModel(): void {
    // Snapshot where each chamber is NOW, so the new layout can be animated from it.
    const fromPos = new Map<string, { x: number; y: number }>();
    for (const [name, c] of model.chambers) fromPos.set(name, { x: c.x, y: c.y });
    model = buildLabyrinth(notes, { state: getLabyrinthState(), prevPartition: lastPartition });
    lastPartition = model.partition;
    indexModel();
    autoAnchorAll(); // keep the layout stable across note changes (invisible, auto)
    suggestions = computeSuggestions(model, getLabyrinthState());
    // Keep the selection if its chamber survived the rebuild (so a just-created
    // note stays spotlit), else clear it.
    if (!(selected && model.chambers.has(selected))) selected = null;
    renderSuggestions();
    sceneKey = ''; // model changed — force a scene re-render (model isn't in sceneKeyOf)
    animatePositions(fromPos); // glide chambers from their old spots to the new layout
    updateThreadBadge(); // refresh the conflicted-copy count on the Thread control
  }

  async function rebuildFromStorage(): Promise<void> {
    const fresh = await window.NoteStorage.getAllNotes();
    notes = fresh.filter((n: { name: string }) => !isReservedNote(n.name));
    rebuildModel();
  }

  // ── Undo / redo (keyboard: ⌘/Ctrl+Z, ⌘/Ctrl+Shift+Z) ──────────────────────
  function pushAction(a: UserAction): void {
    undoStack.push(a);
    redoStack = [];
  }
  async function doUndo(): Promise<void> {
    const a = undoStack.pop();
    if (!a) return;
    await a.undo();
    redoStack.push(a);
  }
  async function doRedo(): Promise<void> {
    const a = redoStack.pop();
    if (!a) return;
    await a.redo();
    undoStack.push(a);
  }

  function stateAction(mutate: () => void): void {
    const before = JSON.parse(JSON.stringify(getLabyrinthState())) as LabyrinthState;
    mutate();
    const after = JSON.parse(JSON.stringify(getLabyrinthState())) as LabyrinthState;
    rebuildModel();
    pushAction({
      undo: () => {
        replaceLabyrinthState(before);
        rebuildModel();
      },
      redo: () => {
        replaceLabyrinthState(after);
        rebuildModel();
      },
    });
  }

  // ── Placement override (drag a chamber to another cluster) ────────────────
  // Anchor seeds capture only REAL members — a phantom in the seed would be
  // stripped on reload (destabilising re-match) and raise spurious drift nags.
  const realSeed = (nb: Neighborhood): string[] =>
    nb.memberNames.filter((n) => !model.chambers.get(n)?.missing);

  function moveToNeighborhood(note: string, nb: Neighborhood): void {
    const c = model.chambers.get(note);
    if (nb.isMisc || c?.missing) return; // can't pin a phantom or move into Misc
    stateAction(() => {
      const existing = getLabyrinthState().anchored[nb.id];
      const seed = existing ? [...existing.seed] : realSeed(nb);
      if (!seed.includes(note)) seed.push(note);
      setAnchor(nb.id, { col: nb.col, row: nb.row, seed });
      setLock(note, nb.id);
    });
  }

  // Auto-pin every real cluster to its current grid slot so the layout stays put
  // as notes are added/edited — anchoring is automatic + invisible now (no UI),
  // and Reset clears it for a fresh re-cluster. Only writes for genuinely new,
  // not-yet-pinned clusters, so it converges; the local-write echo is guarded in
  // labyrinth-state, so this can't loop with the change-driven rebuild.
  function autoAnchorAll(): void {
    const anchored = getLabyrinthState().anchored;
    for (const nb of model.neighborhoods) {
      if (nb.isMisc || anchored[nb.id]) continue;
      setAnchor(nb.id, { col: nb.col, row: nb.row, seed: realSeed(nb) });
    }
  }

  async function createMissingNote(name: string): Promise<void> {
    if (_writing || isReservedNote(name)) return;
    _writing = true;
    try {
      if ((await window.NoteStorage.getNote(name)) !== null) return;
      const content = `# ${name}\n\n`;
      await window.NoteStorage.setNote(name, content);
      await rebuildFromStorage();
      pushAction({
        undo: async () => {
          // Only reverse our OWN create — if the note was edited since, leave it.
          if ((await window.NoteStorage.getNote(name)) === content) {
            await window.NoteStorage.trashNote(name);
            await rebuildFromStorage();
          }
        },
        redo: async () => {
          await window.NoteStorage.setNote(name, content);
          await rebuildFromStorage();
        },
      });
    } finally {
      _writing = false;
    }
  }

  // Select a chamber purely to PIN the highlight (the dim-spotlight). There is no
  // inspector card: double-click opens a real note / creates an uncreated one
  // (see onDblClick); a single click just selects it to keep its web spotlit.
  function selectChamber(c: Chamber | null): void {
    selected = c ? c.name : null;
    requestDraw();
  }

  function openNote(name: string): Promise<void> {
    const current = getCurrentFileName();
    if (current && !getLinkedNoteChain().includes(current)) {
      getLinkedNoteChain().unshift(current);
      saveChain();
    }
    return loadNote(name, true);
  }

  // Zoom-IN: the map zooms into the chosen chamber while the target note grows
  // OUT of that node to fill the screen — the visual reverse of the zoom-OUT
  // intro, so the node you tap "becomes" the note you land in. The note's content
  // only exists after loadNote, so we pre-shrink the (still-hidden) editor onto
  // the node, reveal it, THEN tween the camera + note together on one clock.
  // Ignores re-fire while a tween runs; plain open for a missing/phantom node.
  async function zoomToChamberThenOpen(name: string): Promise<void> {
    if (cameraTweenId != null) return;
    const c = model.chambers.get(name);
    if (!c || c.missing) {
      openNote(name);
      return;
    }
    const end = nodeFillTarget(c, 0.92); // the node-fill pose the camera tweens TO
    const s0 = end.scale;
    container.style.pointerEvents = 'none';
    applyBillboard(c, s0, camera, 'in', 0); // pre-shrink the hidden editor onto the node (invisible)
    setZoomTransition(true);
    await openNote(name); // loadNote → note-manager reveals the already-tiny note + adds .zoom-billboard
    animateCamera(end, {
      focus: { x: c.x, y: c.y }, // pivot on the node so it glides smoothly to centre
      onFrame: (e, cam) => applyBillboard(c, s0, cam, 'in', e),
      onDone: () => {
        resetBillboard(); // note now fills the screen exactly where the settled editor sits
        previewDiv.style.display = 'none'; // map fully covered → drop it
        document.getElementById('editor-section')?.classList.remove('zoom-billboard');
        container.style.pointerEvents = '';
      },
    });
  }

  // ── Suggestion queue ──────────────────────────────────────────────────────
  function renderSuggestions(): void {
    if (!suggestions.length) {
      suggestPanel.style.display = 'none';
      return;
    }
    suggestHead.textContent = `Suggestions (${suggestions.length})`;
    suggestList.render(
      suggestions.map((s) =>
        suggestionCell(
          s,
          () => acceptSuggestion(s),
          () => declineSuggestion(s)
        )
      )
    );
    suggestPanel.style.display = 'block';
  }

  // The only suggestion type is 'oversize': Accept ('Split') drops the anchor so the big cluster
  // re-clusters freely; Decline ('Keep') silences it for this neighborhood.
  function acceptSuggestion(s: Suggestion): void {
    stateAction(() => removeAnchor(s.nbId));
  }

  function declineSuggestion(s: Suggestion): void {
    stateAction(() => addDeclined(s.key));
  }

  // ── Thread (node-by-node alias propagation/creation + dedup cleanup) ───────
  function startThread(): void {
    const declined = new Set(getLabyrinthState().declined);
    const isExcluded = (n: string) => isReservedNote(n) || isDenyListed(n);
    // Exact-duplicate groups → merge items (cleanup, kept alongside the alias walk).
    const groups = duplicateContentGroups(notes, { isExcluded });
    const groupOf = new Map<string, number>();
    groups.forEach((g, i) => {
      for (const n of g.names) groupOf.set(n, i);
    });
    const contentOf = new Map(notes.map((n) => [n.name, n.content]));
    // Alias dictionary from the vault's OWN links — no inference. Skip reserved
    // notes only (daily notes are where aliases like "matt" actually live).
    const aliasIdx = buildAliasIndex(notes, { isExcluded: isReservedNote });
    noteDatalist.innerHTML = notes
      .map((n) => `<option value="${escapeHtml(n.name, { forAttr: true })}"></option>`)
      .join('');

    // Walk the similar-pair scan once: near-dups become merge-diffs; every other
    // pair feeds its shared terms to each note — the raw material for CREATE
    // suggestions (what related notes have in common that isn't an alias yet).
    const mergeDiffs: ThreadItem[] = [];
    const sharedWith = new Map<string, Map<string, number>>();
    const noteShare = (note: string, term: string) => {
      let m = sharedWith.get(note);
      if (!m) {
        m = new Map();
        sharedWith.set(note, m);
      }
      m.set(term, (m.get(term) ?? 0) + 1);
    };
    for (const p of topSimilarUnlinkedPairs(notes, model.adjacency, { declined, limit: 400 })) {
      if (groupOf.has(p.a) && groupOf.get(p.a) === groupOf.get(p.b)) continue;
      // Only the top-cosine pairs can be near-duplicates — gate the costly char-level
      // bodySimilarity so it runs on a handful of pairs, not all 400.
      if (p.score >= NEAR_DUP_COSINE && !isExcluded(p.a) && !isExcluded(p.b)) {
        const ca = contentOf.get(p.a);
        const cb = contentOf.get(p.b);
        const bsim = ca != null && cb != null ? bodySimilarity(ca, cb) : 0;
        if (bsim >= NEAR_DUP_SIMILARITY) {
          // Keep the longer note by default — it tends to be the more complete one.
          const keep = (ca?.length ?? 0) >= (cb?.length ?? 0) ? p.a : p.b;
          mergeDiffs.push({ kind: 'mergeDiff', a: p.a, b: p.b, keep, combine: false, score: bsim });
          continue;
        }
      }
      for (const t of p.sharedTerms) {
        noteShare(p.a, t);
        noteShare(p.b, t);
      }
    }

    // Per-note suggestions: propagate aliases mentioned bare here; offer to create
    // an alias for a proper-noun term shared with similar notes that isn't linked.
    const aliasNotes: Array<Extract<ThreadItem, { kind: 'aliasNote' }>> = [];
    // Compile each alias term's word-boundary regex ONCE (not once per note): the
    // per-note loop below tests every alias against the masked body, so compiling
    // inside it was O(notes × aliases) RegExp builds. Mirrors hasBareTerm exactly
    // (non-global .test → no lastIndex state; aliasIdx terms are always non-empty).
    const aliasRes: Array<[string, Set<string>, RegExp]> = [];
    for (const [term, set] of aliasIdx) {
      aliasRes.push([term, set, new RegExp(`\\b${escapeRegExp(term)}\\b`, 'i')]);
    }
    for (const n of notes) {
      if (isExcluded(n.name)) continue;
      const content = n.content;
      // Mask the body ONCE, then test every alias/shared term against the masked
      // copy — avoids re-masking (4 regex) per term across the whole alias index.
      const masked = maskedBody(content).masked;
      const props: PropSuggestion[] = [];
      for (const [term, set, re] of aliasRes) {
        if (!re.test(masked)) continue;
        if (declined.has(aliasDeclineKey(n.name, term))) continue; // dismissed via Next
        const targets = resolveAlias(set).filter((t) => t !== n.name);
        if (targets.length) props.push({ term, targets });
      }
      const creations: CreateSuggestion[] = [];
      const shareMap = sharedWith.get(n.name);
      if (shareMap) {
        for (const [term, shared] of shareMap) {
          if (shared < 2) continue; // shared with ≥2 similar notes — a real recurring term
          if (aliasIdx.has(term)) continue; // already an alias → a propagation, not new
          if (declined.has(aliasDeclineKey(n.name, term))) continue; // dismissed via Next
          if (!isProperNounIn(content, term)) continue; // names/places, not common words
          if (!hasBareTerm(masked, term)) continue;
          creations.push({ term, shared });
        }
        creations.sort((a, b) => b.shared - a.shared);
        creations.length = Math.min(creations.length, 3); // cap discovery noise per note
      }
      if (props.length || creations.length) {
        aliasNotes.push({ kind: 'aliasNote', note: n.name, props, creations });
      }
    }
    // Notes with the most to do first; then dedup cleanup.
    aliasNotes.sort(
      (a, b) => b.props.length + b.creations.length - (a.props.length + a.creations.length)
    );
    // Conflicts (a note + its sync "conflicted copy") are walked FIRST — they're data-integrity
    // issues. `notes` is already reserved-note-filtered, so only real conflicts surface here.
    const conflicts = findConflictedCopies(notes).map(
      (p): ThreadItem => ({ kind: 'conflict', base: p.base, copy: p.copy, resolution: 'merge' })
    );
    const items: ThreadItem[] = [
      ...conflicts,
      ...aliasNotes,
      ...mergeDiffs,
      ...groups.map((g): ThreadItem => ({ kind: 'merge', names: g.names, keep: g.names[0] })),
    ];
    thread = items.length ? { items, idx: 0 } : null;
    resetThreadStepState();
    activateThreadSurface(); // reveal the docked Thread surface / mobile bottom sheet
    renderThread();
    requestDraw();
  }

  /** A proper-noun signal for CREATE offers: `term` appears Capitalised MID-sentence
   *  (preceded by a lowercase letter or comma), not merely at a heading or sentence
   *  start — so common words ("ensure", "decisions") that only capitalise at line
   *  starts don't masquerade as names. */
  function isProperNounIn(content: string, term: string): boolean {
    const cap = escapeRegExp(term.charAt(0).toUpperCase() + term.slice(1));
    return new RegExp(`[a-z,]\\s+${cap}\\b`).test(content);
  }

  function focusItem(item: ThreadItem): void {
    const cs = threadItemNames(item)
      .map((n) => model.chambers.get(n))
      .filter((c): c is Chamber => Boolean(c));
    if (!cs.length) {
      _focusCam = null;
      return;
    }
    const pad = 120;
    // Animate the zoom IN (mirrors the zoom-OUT on exit). Remember the target so the
    // card positions against the node's final spot rather than its mid-zoom spot.
    const target = fitBoxTarget({
      minX: Math.min(...cs.map((c) => c.x)) - pad,
      minY: Math.min(...cs.map((c) => c.y)) - pad,
      maxX: Math.max(...cs.map((c) => c.x)) + pad,
      maxY: Math.max(...cs.map((c) => c.y)) + pad,
    });
    _focusCam = target;
    animateCamera(target);
  }

  // ── Thread walk rendering (side-panel #thread-panel surface) ────────────────
  /** Swap the panel's HTML, preserving the body scroll position — a checkbox
   *  toggle re-renders the whole step and we don't want the list to jump. */
  function setThreadHtml(html: string): void {
    const prev = threadPanel.querySelector<HTMLElement>('.thread-body')?.scrollTop ?? 0;
    threadPanel.innerHTML = html;
    const body = threadPanel.querySelector<HTMLElement>('.thread-body');
    if (body) body.scrollTop = prev;
  }
  const threadIsMobile = (): boolean => mobileMediaQuery.matches;
  const threadGrip = (): string => (threadIsMobile() ? '<div class="thread-grip"></div>' : '');
  function stepHeader(kind: string, cur: number, total: number): string {
    const w = Math.round((cur / total) * 100);
    return (
      '<div class="thread-head"><div class="thread-meta">' +
      `<span class="thread-kind">${escapeHtml(kind)}</span>` +
      `<span class="thread-count">${cur} / ${total}</span></div>` +
      `<div class="thread-track"><span style="width:${w}%"></span></div></div>`
    );
  }
  // Footer. `nav` false (the empty state) drops the per-step Skip/Next controls,
  // leaving just Done. Skip = pass on this suggestion; Next = affirmatively continue
  // to the next one — both advance, differentiated by weight in the CSS.
  function stepActions(primary = '', nav = true): string {
    const navBtns = nav
      ? '<button class="thread-skip" type="button">Skip</button>' +
        '<button class="thread-next" type="button">Next</button>' +
        '<span class="thread-spacer"></span>'
      : '<span class="thread-spacer"></span>';
    return `<div class="thread-actions">${primary}${navBtns}<button class="thread-done" type="button">Done</button></div>`;
  }
  const noteContent = (name: string): string =>
    notes.find((n: { name: string }) => n.name === name)?.content ?? '';

  /** A collapsible fold (a section OR a term row). `key` drives the shared
   *  `threadOpen` Set + the delegated toggle handler; `headHtml` is whatever sits
   *  before the count badge. No disclosure triangle — the row itself is the toggle. */
  function fold(key: string, headHtml: string, badge: number, bodyHtml: string): string {
    const open = threadOpen.has(key);
    return (
      `<div class="tw-fold${open ? ' is-open' : ''}">` +
      `<div class="tw-fold-head" data-toggle="${escapeHtml(key, { forAttr: true })}">` +
      `${headHtml}<span class="tw-badge">${badge}</span></div>` +
      (open ? `<div class="tw-fold-body">${bodyHtml}</div>` : '') +
      '</div>'
    );
  }

  /** One occurrence line: a checkbox + the note line with the term marked (a link
   *  when applied, a bare highlight otherwise). `data-occ` is document order —
   *  stable across the re-scan a toggle triggers. */
  function occLine(span: TermSpan, termKey: string, occIdx: number, disabled: boolean): string {
    const { body, index, length, matched, linked } = span;
    const ls = body.lastIndexOf('\n', index - 1) + 1;
    let le = body.indexOf('\n', index);
    if (le === -1) le = body.length;
    // Unwrap link/wiki markup in the surrounding context so a NEIGHBOURING mention
    // reads as plain text ("dad"), not raw "[dad](Dad)"; then keep the term near the
    // start so it survives the 2-line clamp (single-line notes cram many on one line).
    const plain = (s: string): string =>
      s.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/\[\[([^\]]*)\]\]/g, '$1');
    let pre = plain(body.slice(ls, index));
    let post = plain(body.slice(index + length, le));
    if (pre.length > 26) pre = `…${pre.slice(-26)}`;
    if (post.length > 60) post = `${post.slice(0, 60)}…`;
    const mark = `<span class="${linked ? 'tw-link' : 'tw-bare'}">${escapeHtml(matched)}</span>`;
    return (
      `<label class="tw-line${linked ? ' is-linked' : ''}" data-term="${escapeHtml(termKey, { forAttr: true })}" data-occ="${occIdx}">` +
      `<input type="checkbox"${linked ? ' checked' : ''}${disabled ? ' disabled' : ''}>` +
      `<span class="tw-ctx">${escapeHtml(pre)}${mark}${escapeHtml(post)}</span></label>`
    );
  }

  /** Currently-chosen target note for a prop/create term (persisted across
   *  re-renders so a picked target / typed note survives a checkbox toggle). */
  function termTarget(kind: 'prop' | 'create', i: number, fallback: string): string {
    return threadTargets.get(`${kind}:${i}`) ?? fallback;
  }
  /** Scan a term's occurrences (bare + already-linked-to-target) and render them.
   *  With no target (a Create term not yet named) the checkboxes are disabled. */
  function termLines(
    content: string,
    key: string,
    term: string,
    target: string
  ): { count: number; html: string } {
    const href = target ? linkTarget(target) : '';
    const spans = findTermSpans(content, term, href);
    return { count: spans.length, html: spans.map((s, j) => occLine(s, key, j, !href)).join('') };
  }
  /** The target control for a propagate term — a plain label, or a picker when the
   *  alias resolves to several notes. */
  function propTargetControl(i: number, p: PropSuggestion): { target: string; html: string } {
    const chosen = termTarget('prop', i, p.targets[0]);
    if (p.targets.length > 1) {
      const opts = p.targets
        .map(
          (t) =>
            `<option value="${escapeHtml(t, { forAttr: true })}"${t === chosen ? ' selected' : ''}>${escapeHtml(t)}</option>`
        )
        .join('');
      return {
        target: chosen,
        html: `<select class="tw-target-select" data-term="prop:${i}">${opts}</select>`,
      };
    }
    return { target: p.targets[0], html: `<span class="tw-to">${escapeHtml(p.targets[0])}</span>` };
  }

  function resetThreadStepState(): void {
    threadOpen.clear();
    threadTargets.clear();
    mobileStepIdx = 0;
  }

  /** Render the current step (dispatch by kind) WITHOUT re-running the camera focus
   *  — used after a disclosure/target/scroll change. */
  function rerenderStep(): void {
    if (!thread) {
      setThreadHtml(
        threadGrip() +
          '<div class="thread-body"><div class="thread-empty">No more suggestions.</div></div>' +
          stepActions('', false)
      );
      return;
    }
    const item = thread.items[thread.idx];
    if (item.kind === 'aliasNote') renderAliasStep(item);
    else if (item.kind === 'merge') renderMergeStep(item);
    else if (item.kind === 'mergeDiff') renderMergeDiffStep(item);
    else if (item.kind === 'conflict') renderConflictStep(item);
  }

  /** Advance to the current step: skip items an earlier merge trashed, zoom the map
   *  to the note(s) under review, then render. */
  function renderThread(): void {
    if (!thread) {
      rerenderStep();
      return;
    }
    const item = thread.items[thread.idx];
    if (item.kind === 'merge') {
      item.names = item.names.filter((n) => model.chambers.has(n));
      if (item.names.length < 2) {
        advanceThread();
        return;
      }
      if (!item.names.includes(item.keep)) item.keep = item.names[0];
    } else if (threadItemNames(item).some((n) => !model.chambers.has(n))) {
      advanceThread();
      return;
    }
    focusItem(item);
    rerenderStep();
  }

  async function applyCurrentStep(): Promise<void> {
    if (!thread) return;
    const item = thread.items[thread.idx];
    if (item.kind === 'merge')
      await applyMerge(
        item.keep,
        item.names.filter((n) => n !== item.keep)
      );
    else if (item.kind === 'mergeDiff') await applyMergeDiff(item);
    else if (item.kind === 'conflict') await applyConflict(item);
  }

  /** Write one alias link/unlink: persist, mirror into the open editor, record the
   *  local echo (so the conflict sweep never forks it), and signal live views. */
  async function persistAlias(note: string, content: string): Promise<void> {
    await window.NoteStorage.setNote(note, content);
    const nn = notes.find((n: { name: string }) => n.name === note);
    if (nn) nn.content = content;
    if (getCurrentFileName() === note) {
      NoteEditor.replaceAll(content);
      reflectActiveView();
    }
    recordLocalEcho(note, content);
    window.dispatchEvent(new CustomEvent('notes:replaced', { detail: { note } }));
  }

  /** Link (or unlink) occurrence `occIdx` of a prop/create term; the notes:replaced
   *  signal then re-renders the step. Auto-apply — the checkbox IS the action. */
  async function toggleOccurrence(
    item: Extract<ThreadItem, { kind: 'aliasNote' }>,
    key: string,
    occIdx: number,
    wantLink: boolean
  ): Promise<void> {
    if (_writing) return;
    const [kind, iStr] = key.split(':');
    const i = Number(iStr);
    let term = '';
    let target = '';
    if (kind === 'prop') {
      const p = item.props[i];
      if (!p) return;
      term = p.term;
      target = termTarget('prop', i, p.targets[0]);
    } else {
      const c = item.creations[i];
      if (!c) return;
      term = c.term;
      target = termTarget('create', i, '');
    }
    const href = target ? linkTarget(target) : '';
    if (!href) return; // a Create term with no target yet — nothing to link to
    _writing = true;
    try {
      const content = noteContent(item.note);
      const span = findTermSpans(content, term, href)[occIdx];
      if (!span) return;
      let next: string | null = null;
      if (wantLink && !span.linked)
        next = replaceBareHits(content, term, `[${term}](${href})`, [span]);
      else if (!wantLink && span.linked) next = unlinkSpan(span);
      if (next != null) await persistAlias(item.note, next);
    } finally {
      _writing = false;
    }
  }

  /** Re-render the open alias step when its note changes (our own link write, or an
   *  external edit). Merge/conflict steps rebuild via applyMerge → advanceThread. */
  async function refreshAliasStep(note?: string): Promise<void> {
    if (!thread) return;
    const item = thread.items[thread.idx];
    if (item.kind !== 'aliasNote') return;
    if (note && note !== item.note) return;
    const fresh = (await window.NoteStorage.getNote(item.note)) ?? '';
    const nn = notes.find((n: { name: string }) => n.name === item.note);
    if (nn) nn.content = fresh;
    renderAliasStep(item);
  }

  /** Next: dismiss the current note's suggested terms so they don't re-nag on a
   *  later walk (a NEW term in the same note still surfaces — the decline is per
   *  note+term). Skip leaves them to reappear. Fully-linked terms are moot (they no
   *  longer match), so declining the whole shown set == declining the un-actioned ones. */
  function declineCurrentAlias(): void {
    if (!thread) return;
    const item = thread.items[thread.idx];
    if (item.kind !== 'aliasNote') return;
    for (const p of item.props) addDeclined(aliasDeclineKey(item.note, p.term));
    for (const c of item.creations) addDeclined(aliasDeclineKey(item.note, c.term));
  }

  // Delegated #thread-panel handlers (added once per mount, removed on teardown).
  function onThreadClick(e: Event): void {
    const el = e.target as HTMLElement;
    const toggle = el.closest('[data-toggle]');
    if (toggle && threadPanel.contains(toggle) && !el.closest('input, select, .tw-line')) {
      const key = toggle.getAttribute('data-toggle') ?? '';
      if (threadOpen.has(key)) threadOpen.delete(key);
      else threadOpen.add(key);
      rerenderStep();
      return;
    }
    if (el.closest('.thread-skip')) advanceThread();
    else if (el.closest('.thread-next')) {
      declineCurrentAlias();
      advanceThread();
    } else if (el.closest('.thread-done')) closeThread();
    else if (el.closest('.thread-apply')) void applyCurrentStep();
    else if (el.closest('.thread-pager-prev')) {
      mobileStepIdx -= 1;
      rerenderStep();
    } else if (el.closest('.thread-pager-next')) {
      mobileStepIdx += 1;
      rerenderStep();
    }
  }
  function onThreadChange(e: Event): void {
    if (!thread) return;
    const item = thread.items[thread.idx];
    const el = e.target as HTMLElement;
    if (el.matches('.tw-line input')) {
      if (item.kind !== 'aliasNote') return;
      const label = el.closest('.tw-line') as HTMLElement | null;
      if (!label) return;
      void toggleOccurrence(
        item,
        label.dataset.term ?? '',
        Number(label.dataset.occ),
        (el as HTMLInputElement).checked
      );
    } else if (el.matches('.tw-target-select, .tw-target-input')) {
      threadTargets.set(
        (el as HTMLElement).dataset.term ?? '',
        (el as HTMLInputElement).value.trim()
      );
      rerenderStep();
    } else if (el.matches('.tw-keep-radio')) {
      if (item.kind === 'merge' || item.kind === 'mergeDiff')
        item.keep = (el as HTMLInputElement).value;
    } else if (el.matches('.tw-res-radio')) {
      if (item.kind === 'conflict')
        item.resolution = (el as HTMLInputElement).value as 'merge' | 'mine' | 'theirs';
    } else if (el.matches('.tw-combine')) {
      if (item.kind === 'mergeDiff') {
        item.combine = (el as HTMLInputElement).checked;
        threadPanel.querySelector('.tw-warn')?.classList.toggle('is-hidden', item.combine);
      }
    }
  }

  /** Alias step — collapsible Propagate/Create sections (desktop) or a one-at-a-time
   *  pager (mobile). Each occurrence links/unlinks live via its checkbox; there is no
   *  find-and-replace panel any more — the review happens in context, here. */
  function renderAliasStep(item: Extract<ThreadItem, { kind: 'aliasNote' }>): void {
    if (!thread) return;
    const content = noteContent(item.note);
    const subject = `<div class="thread-subject">Consolidating <b>${escapeHtml(item.note)}</b></div>`;
    const inner = threadIsMobile() ? aliasMobile(item, content) : aliasDesktop(item, content);
    setThreadHtml(
      threadGrip() +
        stepHeader('Alias link', thread.idx + 1, thread.items.length) +
        `<div class="thread-body">${subject}${inner}</div>` +
        stepActions()
    );
    requestDraw();
  }

  /** Desktop: two collapsible sections (Propagate / Create), each collapsed by
   *  default with a term count; a term expands to its in-context occurrence lines. */
  function aliasDesktop(item: Extract<ThreadItem, { kind: 'aliasNote' }>, content: string): string {
    const propRows = item.props
      .map((p, i) => {
        const key = `prop:${i}`;
        const tc = propTargetControl(i, p);
        const { count, html } = termLines(content, key, p.term, tc.target);
        const head =
          `<span class="tw-term-label"><b class="tw-term-name">${escapeHtml(p.term)}</b>` +
          `<span class="tw-arrow">→</span>${tc.html}</span>`;
        return fold(key, head, count, html);
      })
      .join('');
    const createRows = item.creations
      .map((c, i) => {
        const key = `create:${i}`;
        const target = termTarget('create', i, '');
        const input =
          `<input class="tw-target-input" data-term="create:${i}" list="labyrinth-note-names" ` +
          `value="${escapeHtml(target, { forAttr: true })}" placeholder="link to…">`;
        const { count, html } = termLines(content, key, c.term, target);
        const head =
          `<span class="tw-term-label"><b class="tw-term-name">${escapeHtml(c.term)}</b>` +
          `<span class="tw-arrow">→</span>${input}</span>`;
        return fold(key, head, count, html);
      })
      .join('');
    return (
      (item.props.length
        ? fold(
            'sect:propagate',
            '<span class="tw-fold-label">Propagate</span>',
            item.props.length,
            propRows
          )
        : '') +
      (item.creations.length
        ? fold(
            'sect:create',
            '<span class="tw-fold-label">Create</span>',
            item.creations.length,
            createRows
          )
        : '')
    );
  }

  /** Mobile: one suggestion (term + its occurrence lines) at a time, with a pager —
   *  the nested accordion doesn't fit the smaller surface. */
  function aliasMobile(item: Extract<ThreadItem, { kind: 'aliasNote' }>, content: string): string {
    type Sug = { key: string; term: string; head: string; target: string; label: string };
    const sug: Sug[] = [];
    item.props.forEach((p, i) => {
      const tc = propTargetControl(i, p);
      sug.push({
        key: `prop:${i}`,
        term: p.term,
        target: tc.target,
        label: 'Propagate',
        head: `<b class="tw-term-name">${escapeHtml(p.term)}</b><span class="tw-arrow">→</span>${tc.html}`,
      });
    });
    item.creations.forEach((c, i) => {
      const target = termTarget('create', i, '');
      const input =
        `<input class="tw-target-input" data-term="create:${i}" list="labyrinth-note-names" ` +
        `value="${escapeHtml(target, { forAttr: true })}" placeholder="link to…">`;
      sug.push({
        key: `create:${i}`,
        term: c.term,
        target,
        label: 'Create',
        head: `<b class="tw-term-name">${escapeHtml(c.term)}</b><span class="tw-arrow">→</span>${input}`,
      });
    });
    if (!sug.length) return '<div class="thread-empty">Nothing to link here.</div>';
    mobileStepIdx = Math.max(0, Math.min(mobileStepIdx, sug.length - 1));
    const s = sug[mobileStepIdx];
    const { html } = termLines(content, s.key, s.term, s.target);
    const lines = html || '<div class="thread-empty">No occurrences.</div>';
    const pager =
      '<div class="thread-pager">' +
      `<button class="thread-pager-prev" type="button"${mobileStepIdx === 0 ? ' disabled' : ''} aria-label="Previous suggestion">‹</button>` +
      `<span class="thread-pager-pos">${escapeHtml(s.label)} · ${mobileStepIdx + 1} of ${sug.length}</span>` +
      `<button class="thread-pager-next" type="button"${mobileStepIdx === sug.length - 1 ? ' disabled' : ''} aria-label="Next suggestion">›</button>` +
      '</div>';
    return `<div class="tw-mob-head">${s.head}</div><div class="tw-mob-lines">${lines}</div>${pager}`;
  }

  function renderMergeStep(item: Extract<ThreadItem, { kind: 'merge' }>): void {
    if (!thread) return; // always called with thread set; narrows for idx/length below
    const attr = (s: string) => escapeHtml(s, { forAttr: true });
    const options = item.names
      .map(
        (n) =>
          '<label class="tw-opt"><input class="tw-keep-radio" type="radio" name="merge-keep" ' +
          `value="${attr(n)}"${n === item.keep ? ' checked' : ''}><span>${escapeHtml(n)}</span></label>`
      )
      .join('');
    setThreadHtml(
      threadGrip() +
        stepHeader('Merge duplicates', thread.idx + 1, thread.items.length) +
        '<div class="thread-body">' +
        `<div class="thread-hint">${item.names.length} notes share identical content. Keep one title; the rest go to Trash.</div>` +
        `<div class="tw-choice">${options}</div></div>` +
        stepActions('<button class="thread-apply" type="button">Merge</button>')
    );
    requestDraw();
  }

  // Near-duplicate merge: two almost-identical notes. Title pick + a collapsible
  // line-level diff + a "combine both" toggle so no unique content is lost.
  function renderMergeDiffStep(item: Extract<ThreadItem, { kind: 'mergeDiff' }>): void {
    if (!thread) return;
    const attr = (s: string) => escapeHtml(s, { forAttr: true });
    // Stable A-then-B order (independent of which title is kept) so picking a
    // radio just fills it in place — never reorders the list under the cursor.
    const diff = lineDiff(noteContent(item.a), noteContent(item.b));
    const radio = (n: string) =>
      '<label class="tw-opt"><input class="tw-keep-radio" type="radio" name="md-keep" ' +
      `value="${attr(n)}"${n === item.keep ? ' checked' : ''}><span>${escapeHtml(n)}</span></label>`;
    const diffHtml =
      '<div class="labyrinth-md-diff">' +
      `${diffBlock(`Only in ${item.a}`, diff.aOnly, 8)}${diffBlock(`Only in ${item.b}`, diff.bOnly, 8)}</div>`;
    setThreadHtml(
      threadGrip() +
        stepHeader('Near-duplicate', thread.idx + 1, thread.items.length) +
        '<div class="thread-body">' +
        `<div class="thread-hint">${Math.round(item.score * 100)}% identical. Keep one title:</div>` +
        `<div class="tw-choice tw-choice--inline">${radio(item.a)}${radio(item.b)}</div>` +
        fold(
          'sect:diff',
          '<span class="tw-fold-label">Differences</span>',
          diff.aOnly.length + diff.bOnly.length,
          diffHtml
        ) +
        '<label class="tw-opt tw-opt--toggle"><input class="tw-combine" type="checkbox"' +
        `${item.combine ? ' checked' : ''}><span>Combine both (keep all lines)</span></label>` +
        `<div class="tw-warn${item.combine ? ' is-hidden' : ''}">The other note's unique lines will be discarded.</div>` +
        '</div>' +
        stepActions('<button class="thread-apply" type="button">Merge</button>')
    );
    requestDraw();
  }

  async function applyMergeDiff(item: Extract<ThreadItem, { kind: 'mergeDiff' }>): Promise<void> {
    const keep = item.keep;
    const other = keep === item.a ? item.b : item.a;
    let keepNewContent: string | undefined;
    if (item.combine) {
      const keepRaw = (await window.NoteStorage.getNote(keep)) ?? '';
      const otherRaw = (await window.NoteStorage.getNote(other)) ?? '';
      // Preserve the survivor's title line; combine the bodies losslessly.
      const nl = keepRaw.indexOf('\n');
      const hasHeading = nl !== -1 && /^#{1,6}\s/.test(keepRaw);
      const head = hasHeading ? `${keepRaw.slice(0, nl)}\n\n` : '';
      keepNewContent = head + unionMerge(keepRaw, otherRaw);
    }
    await applyMerge(keep, [other], keepNewContent);
  }

  // A note and its sync "conflicted copy": show the LIVE line diff (recomputed each render so
  // sequential resolves stay correct) behind a collapsible fold, and either 3-way merge them or
  // keep one side. An identical copy collapses to a one-click "Remove copy".
  function renderConflictStep(item: Extract<ThreadItem, { kind: 'conflict' }>): void {
    if (!thread) return;
    const diff = lineDiff(noteContent(item.base), noteContent(item.copy));
    const cur = thread.idx + 1;
    const total = thread.items.length;
    if (diff.aOnly.length === 0 && diff.bOnly.length === 0) {
      item.resolution = 'mine'; // removing the redundant copy; base is unchanged either way
      setThreadHtml(
        threadGrip() +
          stepHeader('Conflicted copy', cur, total) +
          '<div class="thread-body">' +
          `<div class="thread-hint">"${escapeHtml(item.base)}" has an identical conflicted copy — safe to remove.</div></div>` +
          stepActions('<button class="thread-apply" type="button">Remove copy</button>')
      );
      requestDraw();
      return;
    }
    const radio = (val: 'merge' | 'mine' | 'theirs', label: string) =>
      '<label class="tw-opt"><input class="tw-res-radio" type="radio" name="conflict-res" ' +
      `value="${val}"${item.resolution === val ? ' checked' : ''}><span>${escapeHtml(label)}</span></label>`;
    const diffHtml =
      '<div class="labyrinth-md-diff">' +
      `${diffBlock(`Only in ${item.base} (yours)`, diff.aOnly, 12)}${diffBlock('Only in the copy', diff.bOnly, 12)}</div>`;
    setThreadHtml(
      threadGrip() +
        stepHeader('Conflicted copy', cur, total) +
        '<div class="thread-body">' +
        `<div class="thread-hint">"${escapeHtml(item.base)}" was saved as a conflicted copy. Choose what to keep:</div>` +
        fold(
          'sect:diff',
          '<span class="tw-fold-label">What differs</span>',
          diff.aOnly.length + diff.bOnly.length,
          diffHtml
        ) +
        `<div class="tw-choice">${radio('merge', 'Merge both (3-way)')}${radio('mine', 'Keep mine')}${radio('theirs', 'Keep the copy')}</div>` +
        '</div>' +
        stepActions('<button class="thread-apply" type="button">Resolve</button>')
    );
    requestDraw();
  }

  async function applyConflict(item: Extract<ThreadItem, { kind: 'conflict' }>): Promise<void> {
    const { base, copy, resolution } = item;
    const NS = window.NoteStorage;
    let keepNewContent: string | undefined;
    if (resolution === 'theirs') {
      // Keep the copy's content under the surviving (base) name.
      keepNewContent = (await NS.getNote(copy)) ?? '';
    } else if (resolution === 'merge') {
      const mine = (await NS.getNote(base)) ?? '';
      const copyRaw = (await NS.getNote(copy)) ?? '';
      const mergeBase = (await NS.getMergeBase?.(base)) ?? null;
      keepNewContent = mergeConflictContent(mergeBase, mine, copyRaw);
    }
    // resolution === 'mine' leaves keepNewContent undefined → base stays as-is, copy is trashed.
    clearPendingConflict(base); // dismiss any open conflict banner for this note
    // applyMerge handles the write, link-repoint, trash, undo/redo, rebuild + advance. The setNote
    // goes through the crypto-storage local-echo ledger, so the sweep won't re-fork it.
    await applyMerge(base, [copy], keepNewContent);
  }

  async function applyMerge(
    keep: string,
    losers: string[],
    keepNewContent?: string
  ): Promise<void> {
    if (_writing) return;
    _writing = true;
    try {
      const NS = window.NoteStorage;
      if (losers.length) {
        // Capture loser contents BEFORE trashing, for undo restore.
        const captured: Array<{ name: string; content: string }> = [];
        for (const n of losers) captured.push({ name: n, content: (await NS.getNote(n)) ?? '' });
        // Re-point inbound links across every surviving note (the survivor's own
        // links to a loser repoint to itself → parseNoteLinks drops the self-link).
        // In combine mode the survivor starts from the merged body, not its current one.
        const writes: Array<{ name: string; before: string; after: string }> = [];
        for (const note of notes) {
          if (losers.includes(note.name)) continue;
          const base = note.name === keep && keepNewContent != null ? keepNewContent : note.content;
          let after = base;
          for (const l of losers) after = repointLinks(after, l, keep);
          if (after !== note.content) writes.push({ name: note.name, before: note.content, after });
        }
        const forward = async () => {
          await Promise.all(writes.map((w) => NS.setNote(w.name, w.after)));
          await Promise.all(losers.map((n) => NS.trashNote(n)));
          await rebuildFromStorage();
        };
        await forward();
        pushAction({
          undo: async () => {
            await Promise.all(writes.map((w) => NS.setNote(w.name, w.before)));
            // setNote re-creates a trashed note (same contract createMissingNote relies on).
            await Promise.all(captured.map((c) => NS.setNote(c.name, c.content)));
            await rebuildFromStorage();
          },
          redo: forward,
        });
      }
    } finally {
      _writing = false;
    }
    advanceThread();
  }

  function advanceThread(): void {
    if (!thread) return;
    thread.idx++;
    if (thread.idx >= thread.items.length) thread = null;
    resetThreadStepState();
    renderThread();
    requestDraw();
  }

  function closeThread(): void {
    thread = null;
    _focusCam = null;
    resetThreadStepState();
    threadPanel.innerHTML = '';
    deactivateThreadSurface();
    // Exiting the walk zooms back out to the default whole-map view (the walk
    // zoomed in on each item via focusItem).
    animateCamera(fitBoxTarget(model.bounds));
  }

  // Re-render the open alias step as its note changes in this tab — our own live
  // link/unlink write (notes:replaced from persistAlias), or an external edit. The
  // detail.note guard keeps an unrelated note's change a no-op. (A direct event
  // beats powersync:change: that one is debounced ~100ms, fires ~twice per write —
  // the local write + its sync echo — and carries no note name.)
  function onNotesChanged(e: Event): void {
    void refreshAliasStep((e as CustomEvent).detail?.note);
  }

  // ── Interaction ───────────────────────────────────────────────────────────
  let dragging = false;
  let moved = false;
  let lastX = 0;
  let lastY = 0;
  let lastPinch: number | null = null;

  function localPoint(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function chamberAt(screenX: number, screenY: number): Chamber | null {
    const wx = screenToWorldX(screenX);
    const wy = screenToWorldY(screenY);
    const slack = 6 / camera.scale;
    let best: Chamber | null = null;
    let bestD2 = Infinity;
    for (const c of model.chambers.values()) {
      const dx = c.x - wx;
      const dy = c.y - wy;
      const d2 = dx * dx + dy * dy;
      const hitR = c.radius + slack;
      if (d2 <= hitR * hitR && d2 < bestD2) {
        bestD2 = d2;
        best = c;
      }
    }
    return best;
  }

  function beginDrag(px: number, py: number, clientX: number, clientY: number): void {
    const c = chamberAt(px, py);
    if (c) {
      // A chamber drag lifts it for a move (resolved on mouseup; a no-move
      // release is a select). Pan is reserved for empty space.
      nodeDrag = { name: c.name, world: { x: screenToWorldX(px), y: screenToWorldY(py) } };
      moved = false;
      return;
    }
    nodeDrag = null; // defensive: a stale lift (e.g. missed touchcancel) must not pan
    dragging = true;
    moved = false;
    lastX = clientX;
    lastY = clientY;
    canvas.style.cursor = 'grabbing';
  }

  function resolveDrop(px: number, py: number): void {
    if (!nodeDrag) return;
    const source = nodeDrag.name;
    nodeDrag = null;
    dropTarget = null;
    if (!moved) {
      selectChamber(chamberAt(px, py));
      return;
    }
    const nb = neighborhoodAt(screenToWorldX(px), screenToWorldY(py));
    const srcNb = model.chambers.get(source)?.neighborhoodId;
    if (nb && !nb.isMisc && nb.id !== srcNb) moveToNeighborhood(source, nb);
    requestDraw();
  }

  // Shared single-pointer gesture bodies — driven by both mouse and touch.
  function dragNodeTo(clientX: number, clientY: number): void {
    lastX = clientX;
    lastY = clientY;
    const p = localPoint({ clientX, clientY });
    const wx = screenToWorldX(p.x);
    const wy = screenToWorldY(p.y);
    moved = true;
    if (nodeDrag) nodeDrag.world = { x: wx, y: wy };
    dropTarget = neighborhoodAt(wx, wy);
    requestDraw();
  }
  function panBy(clientX: number, clientY: number): void {
    const dx = clientX - lastX;
    const dy = clientY - lastY;
    if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
    camera.panX += dx;
    camera.panY += dy;
    lastX = clientX;
    lastY = clientY;
    requestDraw();
  }

  function onMouseDown(e: MouseEvent): void {
    if ((e.target as HTMLElement).closest('.labyrinth-suggest, .labyrinth-thread')) return;
    const p = localPoint(e);
    beginDrag(p.x, p.y, e.clientX, e.clientY);
  }
  function onMouseMove(e: MouseEvent): void {
    if (nodeDrag) {
      dragNodeTo(e.clientX, e.clientY);
      return;
    }
    if (dragging) {
      panBy(e.clientX, e.clientY);
      return;
    }
    if (isTouchOnly) return;
    // Map hidden behind an open note: this document-level listener still fires on
    // every move, so skip the O(chambers) hit-test until the canvas is visible
    // again (cheap, reflow-free inline-style read).
    if (previewDiv.style.display === 'none') return;
    const p = localPoint(e);
    const c = chamberAt(p.x, p.y);
    const name = c ? c.name : null;
    if (name !== hovered) {
      hovered = name;
      canvas.style.cursor = name ? 'grab' : 'default';
      requestDraw(); // instant — hover only repaints the pointed label over the cached scene
    }
  }
  function onMouseUp(e: MouseEvent): void {
    const p = localPoint(e);
    if (nodeDrag) {
      resolveDrop(p.x, p.y);
      canvas.style.cursor = hovered ? 'grab' : 'default';
      return;
    }
    if (!dragging) return;
    dragging = false;
    canvas.style.cursor = hovered ? 'grab' : 'default';
    if (!moved) selectChamber(chamberAt(p.x, p.y));
  }
  function onDblClick(e: MouseEvent): void {
    const p = localPoint(e);
    const c = chamberAt(p.x, p.y);
    if (!c) return;
    // Double-click opens a real note (zooming in); on an uncreated note it
    // creates the note (the old inspector "Create note" affordance).
    if (c.missing) void createMissingNote(c.name);
    else zoomToChamberThenOpen(c.name);
  }
  function zoomAt(screenX: number, screenY: number, factor: number): void {
    const wx = screenToWorldX(screenX);
    const wy = screenToWorldY(screenY);
    camera.scale = Math.max(0.02, Math.min(6, camera.scale * factor));
    camera.panX = screenX - wx * camera.scale;
    camera.panY = screenY - wy * camera.scale;
    requestDraw();
  }
  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 20;
    dy = Math.max(-80, Math.min(80, dy));
    const p = localPoint(e);
    zoomAt(p.x, p.y, Math.exp(-dy * 0.0016));
  }

  function touchDist(t: TouchList): number {
    return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  }
  function onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      lastX = t.clientX;
      lastY = t.clientY;
      const p = localPoint(t);
      beginDrag(p.x, p.y, t.clientX, t.clientY);
      lastPinch = null;
    } else if (e.touches.length === 2) {
      dragging = false;
      nodeDrag = null;
      dropTarget = null;
      lastPinch = touchDist(e.touches);
    }
  }
  function onTouchMove(e: TouchEvent): void {
    if (e.touches.length === 1 && nodeDrag) {
      dragNodeTo(e.touches[0].clientX, e.touches[0].clientY);
      e.preventDefault();
    } else if (e.touches.length === 1 && dragging) {
      panBy(e.touches[0].clientX, e.touches[0].clientY);
      e.preventDefault();
    } else if (e.touches.length === 2 && lastPinch !== null) {
      const dist = touchDist(e.touches);
      const p = localPoint({
        clientX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        clientY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      });
      zoomAt(p.x, p.y, dist / lastPinch);
      lastPinch = dist;
      e.preventDefault();
    }
  }
  function onTouchEnd(e: TouchEvent): void {
    if (e.touches.length === 0) {
      // changedTouches holds the lifted finger on touchend; lastX/lastY is the fallback.
      const ct = e.changedTouches[0];
      const p = localPoint(ct ?? { clientX: lastX, clientY: lastY });
      if (nodeDrag) {
        resolveDrop(p.x, p.y);
      } else if (dragging && !moved) {
        selectChamber(chamberAt(p.x, p.y));
      }
      dragging = false;
      lastPinch = null;
    }
  }
  function onTouchCancel(): void {
    // OS-hijacked gesture: drop all in-flight drag/lift state so no ghost lingers
    // and no stray touchend commits an unintended move.
    nodeDrag = null;
    dropTarget = null;
    dragging = false;
    lastPinch = null;
    moved = false;
    requestDraw();
  }
  function onKeyDown(e: KeyboardEvent): void {
    if (!document.getElementById('labyrinth-container')) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (!(e.metaKey || e.ctrlKey)) return;
    const k = e.key.toLowerCase();
    if (k === 'z' && !e.shiftKey) {
      e.preventDefault();
      void doUndo();
    } else if ((k === 'z' && e.shiftKey) || k === 'y') {
      e.preventDefault();
      void doRedo();
    }
  }

  canvas.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  document.addEventListener('keydown', onKeyDown);
  canvas.addEventListener('dblclick', onDblClick);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd, { passive: true });
  canvas.addEventListener('touchcancel', onTouchCancel, { passive: true });
  // Keep the open alias step honest as its note is linked in this tab, exit the walk
  // when the Thread heading / panel-cycle fires thread:exit, and route the panel's
  // clicks/changes through delegation (one listener pair, torn down on unmount).
  window.addEventListener('notes:replaced', onNotesChanged);
  window.addEventListener('thread:exit', closeThread);
  threadPanel.addEventListener('click', onThreadClick);
  threadPanel.addEventListener('change', onThreadChange);

  // ── Controls (Reset / Thread) ───────────────────────────────────────────────
  // The Reset and Thread controls live in the app's contextual toolbar — their
  // descriptors are registered once at module load (see bottom of file) and
  // shown only while the Labyrinth view is active. This mounted instance exposes
  // its actions to those toolbar items through the module-level `_activeControls`
  // binding, set in the mount step below.
  function resetOverrides(): void {
    lastPartition = undefined; // Reset = a deliberate COLD re-cluster (drop the warm-start seed)
    const st = getLabyrinthState();
    if (!Object.keys(st.anchored).length && !Object.keys(st.locks).length && !st.declined.length) {
      rebuildModel(); // nothing to clear, but still apply the cold re-cluster
      return;
    }
    stateAction(() => replaceLabyrinthState({ version: 4, anchored: {}, locks: {}, declined: [] }));
  }

  // Count badge on the toolbar's Thread item when conflicted copies exist — the
  // only at-a-glance signal that there's something to resolve (background-sweep
  // forks aren't surfaced anywhere else).
  function updateThreadBadge(): void {
    updateToolbarBadge('lab-thread', findConflictedCopies(notes).length);
  }

  // ── Mount, fit, paint ─────────────────────────────────────────────────────
  _activeRefresh = rebuildFromStorage;
  _runZoomIntro = runZoomOutIntro;
  autoAnchorAll(); // pin the initial layout (the build at the top skipped this — TDZ on realSeed)
  _activeControls = { reset: resetOverrides, thread: startThread };
  updateThreadBadge();
  renderSuggestions();
  requestAnimationFrame(() => {
    resizeCanvas();
    // Zoom-OUT intro: if we arrived by zooming out of a note, frame that note's
    // chamber and pull back to the whole map, so the map opens "from" the node
    // you were just editing. Consumed once. (When the canvas was already mounted
    // the early-return path above runs this same intro instead.)
    const focusNote = consumePendingLabyrinthFocus();
    if (focusNote) {
      runZoomOutIntro(focusNote);
    } else if (_persistedCamera && _persistedFingerprint === fingerprint) {
      camera.scale = _persistedCamera.scale;
      camera.panX = _persistedCamera.panX;
      camera.panY = _persistedCamera.panY;
      draw();
    } else {
      fitBox(model.bounds);
      draw();
    }
  });

  const ro = new ResizeObserver(() => {
    resizeCanvas();
    requestDraw();
  });
  ro.observe(container);

  const persist = (): void => {
    _persistedCamera = { scale: camera.scale, panX: camera.panX, panY: camera.panY };
    _persistedFingerprint = fingerprint;
  };
  const mo = new MutationObserver(() => {
    if (!document.body.contains(container)) {
      persist();
      if (cameraTweenId != null) cancelAnimationFrame(cameraTweenId); // don't paint a detached canvas
      _activeRefresh = null;
      _runZoomIntro = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('notes:replaced', onNotesChanged);
      window.removeEventListener('thread:exit', closeThread);
      threadPanel.removeEventListener('click', onThreadClick);
      threadPanel.removeEventListener('change', onThreadChange);
      // Leaving the Labyrinth closes an open Thread walk (restores the panel under it).
      if (thread) {
        thread = null;
        threadPanel.innerHTML = '';
        deactivateThreadSurface();
      }
      ro.disconnect();
      mo.disconnect();
    }
  });
  mo.observe(previewDiv, { childList: true, subtree: false });
}
